import { api } from './api'

export interface FreemiumSignupRequest {
  email: string
  full_name: string
  password: string
  role: 'parent' | 'teacher'
  how_did_you_hear?: string
  intended_use?: string
  newsletter_consent?: boolean
}

export interface TrialUpgradeRequest {
  plan_tier: 'basic' | 'premium'
  billing_cycle: 'monthly' | 'yearly'
}

export interface UsageLimitResponse {
  tier: string
  limits: Record<string, number | null>
  current_usage: Record<string, number>
  is_trial: boolean
  trial_ends_at: string | null
  can_upgrade: boolean
}

export interface SubscriptionPlan {
  id: string
  name: string
  tier: string
  description: string
  price_monthly: number | null
  price_yearly: number | null
  currency?: string
  region?: string
  features: Record<string, boolean>
  usage_limits: Record<string, number | null>
  trial_days: number
  recommended?: boolean
}

export interface SubscriptionStatusResponse {
  tier: string
  status: string
  is_trial: boolean
  trial_ends_at: string | null
  plan_name: string | null
  started_at: string | null
  expires_at: string | null
  can_upgrade: boolean
  features: Record<string, boolean>
}

export interface UpgradePromptResponse {
  show_prompt: boolean
  prompt_data?: {
    title: string
    message: string
    benefits: string[]
  }
  current_tier?: string
  has_trial_available?: boolean
}

class FreemiumService {
  private basePath = '/freemium'

  async signupFreemiumUser(data: FreemiumSignupRequest): Promise<{
    message: string
    user_id: string
    email_verification_required: boolean
    tier: string
  }> {
    const response = await api.post(`${this.basePath}/signup`, data)
    return response.data
  }

  async startPremiumTrial(data: TrialUpgradeRequest): Promise<{
    message: string
    trial_ends_at: string
    tier: string
    features_unlocked: string[]
  }> {
    const response = await api.post(`${this.basePath}/trial/start`, data)
    return response.data
  }

  async getUsageLimits(): Promise<UsageLimitResponse> {
    const response = await api.get(`${this.basePath}/usage`)
    return response.data
  }

  async getSubscriptionStatus(): Promise<SubscriptionStatusResponse> {
    const response = await api.get(`${this.basePath}/status`)
    return response.data
  }

  async createSampleData(): Promise<{
    message: string
    data_created: Record<string, string[]>
    next_steps: string[]
  }> {
    const response = await api.post(`${this.basePath}/demo/create-sample-data`)
    return response.data
  }

  async getSubscriptionPlans(region: string = 'global'): Promise<SubscriptionPlan[]> {
    const response = await api.get(`${this.basePath}/plans`, { params: { region } })
    return response.data
  }

  async triggerUpgradePrompt(featureAttempted: string): Promise<UpgradePromptResponse> {
    const response = await api.post(`${this.basePath}/upgrade-prompt`, {
      feature_attempted: featureAttempted
    })
    return response.data
  }

  async verifyEmail(token: string): Promise<{
    message: string
    user_id: string
    is_verified: boolean
  }> {
    const response = await api.post(`${this.basePath}/verify-email`, { token })
    return response.data
  }

  async resendVerificationEmail(email: string): Promise<{
    message: string
    email_sent: boolean
  }> {
    const response = await api.post(`${this.basePath}/resend-verification`, { email })
    return response.data
  }

  // Utility methods for UI
  getUsageColor(percentage: number): string {
    if (percentage >= 90) return 'text-red-600'
    if (percentage >= 75) return 'text-orange-600'
    if (percentage >= 50) return 'text-yellow-600'
    return 'text-green-600'
  }

  getUsageVariant(percentage: number): 'default' | 'destructive' | 'outline' | 'secondary' {
    if (percentage >= 90) return 'destructive'
    if (percentage >= 75) return 'outline'
    return 'default'
  }

  formatUsageText(current: number, limit: number | null): string {
    if (limit === null) return `${current} (unlimited)`
    return `${current}/${limit}`
  }

  calculateDaysRemaining(trialEndsAt: string | null): number {
    if (!trialEndsAt) return 0
    const endDate = new Date(trialEndsAt)
    const now = new Date()
    const diffTime = endDate.getTime() - now.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return Math.max(0, diffDays)
  }

  getFeatureLabel(featureKey: string): string {
    const labels: Record<string, string> = {
      ai_questions_monthly: 'AI Questions per Month',
      exam_uploads_monthly: 'Exam Uploads per Month',
      practice_sessions_daily: 'Practice Sessions per Day',
      storage_mb: 'Storage (MB)',
      students_count: 'Students',
      classes_count: 'Classes'
    }
    return labels[featureKey] || featureKey
  }

  getFeatureIcon(featureKey: string): string {
    const icons: Record<string, string> = {
      ai_questions_monthly: 'brain-circuit',
      exam_uploads_monthly: 'upload',
      practice_sessions_daily: 'check-circle',
      storage_mb: 'hard-drive',
      students_count: 'users',
      classes_count: 'book-open'
    }
    return icons[featureKey] || 'zap'
  }

  getPlanColor(tier: string): string {
    const colors: Record<string, string> = {
      free: 'bg-gray-50 border-gray-200',
      basic: 'bg-blue-50 border-blue-200',
      premium: 'bg-secondary-50 border-secondary-200',
      enterprise: 'bg-secondary-50 border-secondary-200'
    }
    return colors[tier] || 'bg-gray-50 border-gray-200'
  }

  getPlanGradient(tier: string): string {
    const gradients: Record<string, string> = {
      free: 'from-gray-400 to-gray-600',
      basic: 'from-blue-400 to-blue-600',
      premium: 'from-secondary-400 to-secondary-600',
      enterprise: 'from-secondary-400 to-secondary-600'
    }
    return gradients[tier] || 'from-gray-400 to-gray-600'
  }

  // Helper for checking if feature is unlimited
  isUnlimited(featureKey: string, limits: Record<string, number | null>): boolean {
    return limits[featureKey] === null
  }

  // Helper for checking if near limit
  isNearLimit(featureKey: string, currentUsage: Record<string, number>, limits: Record<string, number | null>, threshold = 0.8): boolean {
    const limit = limits[featureKey]
    const current = currentUsage[featureKey] || 0

    if (!limit) return false // Unlimited
    return (current / limit) >= threshold
  }

  // Helper for getting upgrade recommendations
  getUpgradeRecommendation(currentTier: string, plans: SubscriptionPlan[]): SubscriptionPlan | null {
    const tierOrder = ['free', 'basic', 'premium', 'enterprise']
    const currentIndex = tierOrder.indexOf(currentTier)

    if (currentIndex === -1 || currentIndex >= tierOrder.length - 1) {
      return null // Already on highest tier or invalid tier
    }

    const nextTier = tierOrder[currentIndex + 1]
    return plans.find(plan => plan.tier === nextTier) || null
  }

  // Helper for calculating savings
  calculateYearlySavings(monthlyPrice: number, yearlyPrice: number): {
    percentage: number
    amount: number
  } {
    const monthlyTotal = monthlyPrice * 12
    const savings = monthlyTotal - yearlyPrice
    const percentage = (savings / monthlyTotal) * 100

    return {
      percentage: Math.round(percentage),
      amount: Math.round(savings * 100) / 100
    }
  }
}

export const freemiumService = new FreemiumService()
export default freemiumService
